<template>
  <section class="pb-8 pt-0 sm:mx-auto sm:flex sm:min-h-[70vh] sm:max-w-5xl sm:flex-col sm:gap-8 sm:px-6 sm:py-16">
    <div v-if="pageLoading" class="border-y border-white/10 bg-midnight-800/60 px-4 py-5 shadow-glow animate-pulse sm:rounded-3xl sm:border sm:p-6 sm:shadow-glow sm:sm:p-8">
      <div class="border-b border-white/10 pb-5">
        <div class="flex items-center justify-between gap-3">
          <div class="h-6 w-40 rounded bg-white/10"></div>
          <div class="h-10 w-10 rounded-full bg-white/10"></div>
        </div>
        <div class="mt-5 flex items-start gap-4 sm:gap-6">
          <div class="h-24 w-24 shrink-0 rounded-full bg-white/10 sm:h-28 sm:w-28"></div>
          <div class="grid flex-1 grid-cols-3 gap-3 pt-2">
            <div v-for="item in 3" :key="`profile-stat-skeleton-${item}`" class="space-y-2 text-center">
              <div class="mx-auto h-5 w-12 rounded bg-white/10"></div>
              <div class="mx-auto h-3 w-16 rounded bg-white/10"></div>
            </div>
          </div>
        </div>
        <div class="mt-4 space-y-3">
          <div class="h-6 w-48 rounded bg-white/10"></div>
          <div class="h-3 w-full rounded bg-white/10"></div>
          <div class="h-3 w-4/5 rounded bg-white/10"></div>
        </div>
      </div>
      <div class="mt-8 grid grid-cols-3 gap-2 sm:gap-3">
        <div v-for="item in 6" :key="`profile-grid-skeleton-${item}`" class="aspect-square rounded bg-white/10"></div>
      </div>
    </div>

    <div v-else class="contents">
    <div class="overflow-hidden border-y border-white/10 bg-midnight-800/60 shadow-glow sm:rounded-3xl sm:border sm:shadow-glow">
      <div
        class="cover-panel relative border-b border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(125,249,255,0.14),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(251,191,36,0.16),_transparent_28%),linear-gradient(150deg,_rgba(15,23,42,0.98),_rgba(17,24,39,0.94))]"
        :class="[{ 'cover-panel-expanded': isCoverExpanded, 'cursor-zoom-in': displayCoverUrl }]"
        @click="toggleCoverExpanded"
      >
        <img
          v-if="displayCoverUrl"
          :src="displayCoverUrl"
          alt="Profile cover"
          class="absolute inset-0 h-full w-full object-cover"
          :style="{ objectPosition: coverObjectPosition }"
          referrerpolicy="no-referrer"
          crossorigin="anonymous"
        />
        <div class="absolute inset-0 bg-gradient-to-t from-midnight-800 via-midnight-800/45 to-transparent"></div>
        <button
          v-if="displayCoverUrl"
          type="button"
          class="absolute right-4 top-4 z-10 rounded-full border border-white/15 bg-black/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.26em] text-white transition hover:border-white/30 hover:bg-black/35"
          @click.stop="toggleCoverExpanded"
        >
          {{ isCoverExpanded ? $t('profilePage.collapseCover') : $t('profilePage.expandCover') }}
        </button>
        <div class="pointer-events-none absolute left-4 top-4 z-[5] rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-steel-200 sm:left-6">
          {{ isCoverExpanded ? $t('profilePage.tapCoverCollapse') : $t('profilePage.tapCoverExpand') }}
        </div>
        <div class="relative flex h-full items-end justify-between gap-4 px-4 py-5 sm:px-6 sm:py-6 sm:p-8">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-300">{{ $t('profilePage.eyebrow') }}</p>
            <p class="mt-2 text-sm text-steel-300">{{ profile.pinned_status || $t('profilePage.noPinnedStatus') }}</p>
          </div>
          <div class="hidden flex-wrap justify-end gap-2 sm:flex">
            <a
              v-for="link in socialLinks"
              :key="link.label"
              :href="link.href"
              target="_blank"
              rel="noopener noreferrer"
              class="rounded-full border border-white/15 bg-black/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.26em] text-white transition hover:border-white/30 hover:bg-black/30"
            >
              {{ link.label }}
            </a>
          </div>
        </div>
      </div>
    <div class="px-4 py-5 sm:p-6 sm:sm:p-8">
      <div class="border-b border-white/10 pb-5">
        <div class="flex items-center justify-between gap-3">
          <p class="truncate text-xl font-semibold tracking-tight text-white">@{{ handle }}</p>
          <div class="flex items-center gap-2">
            <a
              v-if="instagramUrl"
              :href="instagramUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-steel-300 transition hover:border-white/30 hover:text-white"
              aria-label="Instagram"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="5" ry="5" stroke-width="1.6" />
                <circle cx="12" cy="12" r="4" stroke-width="1.6" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
              </svg>
            </a>
          </div>
        </div>

        <div class="mt-5 flex items-start gap-4 sm:gap-6">
          <div class="instagram-ring h-24 w-24 shrink-0 rounded-full p-[3px] sm:h-28 sm:w-28">
            <div class="h-full w-full overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="displayAvatarUrl"
                :src="displayAvatarUrl"
                alt="Profile avatar"
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-2xl font-semibold text-steel-200 sm:text-3xl">
                {{ initials }}
              </div>
            </div>
          </div>

          <div class="grid flex-1 grid-cols-3 gap-3 pt-2 text-center">
            <div>
              <p class="text-lg font-semibold text-white sm:text-xl">{{ postCount }}</p>
              <p class="text-sm text-steel-400">{{ $t('profilePage.posts') }}</p>
            </div>
            <button type="button" class="rounded-2xl" @click="openFollowModal('followers')">
              <p class="text-lg font-semibold text-white sm:text-xl">{{ followerCountLabel }}</p>
              <p class="text-sm text-steel-400">{{ $t('profilePage.followers') }}</p>
            </button>
            <button type="button" class="rounded-2xl" @click="openFollowModal('following')">
              <p class="text-lg font-semibold text-white sm:text-xl">{{ followingCountLabel }}</p>
              <p class="text-sm text-steel-400">{{ $t('profilePage.following') }}</p>
            </button>
          </div>
        </div>

        <div class="mt-4 space-y-2">
          <div class="flex flex-wrap items-center gap-2">
            <div class="flex min-w-0 items-center gap-2">
              <h1 class="truncate text-xl font-semibold text-white">{{ profile.full_name || $t('profilePage.memberFallback') }}</h1>
              <verified-badge v-if="profile.verified" size="sm" />
            </div>
            <span class="inline-block rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] border" :class="roleBadgeClass">
              {{ roleLabel }}
            </span>
            <span
              class="inline-block rounded-full border px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em]"
              :class="tierBadgeClass"
            >
              {{ memberTierLabel }}
            </span>
          </div>
          <p class="whitespace-pre-line text-sm leading-6 text-steel-200/95">
            {{ profile.bio || $t('profilePage.noBio') }}
          </p>
          <div class="flex flex-wrap gap-x-4 gap-y-2 text-sm text-steel-300">
            <p v-if="profile.pinned_status" class="font-medium text-white">{{ profile.pinned_status }}</p>
            <p v-if="locationLabel">{{ locationLabel }}</p>
            <p>{{ $t('profilePage.joined') }} {{ memberSince }}</p>
            <p>{{ totalLikesLabel }} {{ $t('profilePage.likes') }}</p>
            <p>{{ totalViewsLabel }} {{ $t('profilePage.views') }}</p>
          </div>
        </div>

        <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            v-if="canShowFollowButton"
            type="button"
            class="inline-flex items-center justify-center rounded-xl border px-4 py-3 text-sm font-semibold transition"
            :class="isFollowing ? 'border-white/15 bg-white text-midnight-900 hover:bg-neon-400' : 'border-white/15 bg-white/5 text-white hover:border-white/30 hover:bg-white/10'"
            :disabled="followBusy"
            @click="toggleFollow"
          >
            {{ followBusy ? $t('profilePage.updating') : isFollowing ? $t('profilePage.followingAction') : $t('profilePage.follow') }}
          </button>
          <router-link
            v-if="canMessageUser"
            :to="`/messages/${handle}`"
            class="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
          >
            {{ $t('profilePage.message') }}
          </router-link>
        </div>
        <div v-if="socialLinks.length" class="mt-4 flex flex-wrap gap-2 sm:hidden">
          <a
            v-for="link in socialLinks"
            :key="link.label"
            :href="link.href"
            target="_blank"
            rel="noopener noreferrer"
            class="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white transition hover:border-white/30 hover:bg-white/10"
          >
            {{ link.label }}
          </a>
        </div>
      </div>

      <div
        v-if="profile.status === 'banned'"
        class="mt-5 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100"
      >
        {{ $t('profilePage.banned') }}
      </div>

      <div class="mt-5 overflow-x-auto pb-1">
        <div class="flex min-w-max gap-4">
          <div
            v-for="item in highlightItems"
            :key="item.key"
            class="flex w-20 shrink-0 flex-col items-center gap-2 text-center"
          >
            <div class="instagram-story-ring h-20 w-20 rounded-full p-[2px]">
              <div class="flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-white/10 bg-midnight-900/90">
                <img v-if="item.image" :src="item.image" :alt="item.label" class="h-full w-full object-cover" />
                <svg v-else class="h-7 w-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              </div>
            </div>
            <span class="line-clamp-2 text-xs text-steel-300">{{ item.label }}</span>
          </div>
        </div>
      </div>

      <div class="mt-6 grid gap-6 lg:mt-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div class="flex h-full flex-col gap-5">
          <p class="hidden text-sm uppercase tracking-[0.3em] text-steel-400 sm:block">{{ $t('profilePage.bio') }}</p>
          <p class="hidden whitespace-pre-line break-words text-base text-steel-200/90 sm:block">
            {{ profile.bio || $t('profilePage.noBio') }}
          </p>
          <div class="hidden gap-3 sm:grid sm:grid-cols-3">
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('profilePage.posts') }}</p>
              <p class="mt-2 text-lg font-semibold text-white">{{ postCount }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('profilePage.totalLikes') }}</p>
              <p class="mt-2 text-lg font-semibold text-white">{{ totalLikesLabel }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('profilePage.totalViews') }}</p>
              <p class="mt-2 text-lg font-semibold text-white">{{ totalViewsLabel }}</p>
            </div>
            <button
              type="button"
              class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-left transition hover:border-white/20"
              @click="openFollowModal('followers')"
            >
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('profilePage.followers') }}</p>
              <p class="mt-2 text-lg font-semibold text-white">{{ followerCountLabel }}</p>
            </button>
            <button
              type="button"
              class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-left transition hover:border-white/20"
              @click="openFollowModal('following')"
            >
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('profilePage.following') }}</p>
              <p class="mt-2 text-lg font-semibold text-white">{{ followingCountLabel }}</p>
            </button>
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('profilePage.latestPost') }}</p>
              <p class="mt-2 text-sm font-semibold text-white">{{ latestPostLabel }}</p>
            </div>
          </div>
          <div class="grid gap-3 sm:grid-cols-2">
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Pinned</p>
              <p class="mt-2 text-sm text-white">{{ profile.pinned_status || 'No pinned status yet.' }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Badges</p>
              <div class="mt-2 flex flex-wrap gap-2">
                <span
                  v-for="badge in achievementBadges"
                  :key="badge.label"
                  class="inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]"
                  :class="badge.tone"
                >
                  {{ badge.label }}
                </span>
                <span v-if="!achievementBadges.length" class="text-xs text-steel-400">No badges yet.</span>
              </div>
            </div>
          </div>
          <div v-if="featuredGarageCar" class="rounded-2xl border border-white/10 bg-midnight-900/60 p-4">
            <div class="flex items-center justify-between gap-3">
              <div>
                <p class="text-[10px] uppercase tracking-[0.3em] text-amber-300/85">Featured Build</p>
                <p class="mt-2 text-lg font-semibold text-white">{{ garageCarTitle(featuredGarageCar) }}</p>
                <p class="mt-1 text-sm text-steel-300">{{ featuredGarageCar.trim || featuredGarageCar.buildStatusLabel }}</p>
              </div>
              <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-steel-300">
                {{ featuredGarageCar.year || 'Year TBD' }}
              </span>
            </div>
            <div class="mt-4 grid gap-3 sm:grid-cols-3">
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Engine</p>
                <p class="mt-2 text-sm text-white">{{ featuredGarageCar.engine || 'Not added yet' }}</p>
              </div>
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Drivetrain</p>
                <p class="mt-2 text-sm text-white">{{ featuredGarageCar.drivetrain || 'Not added yet' }}</p>
              </div>
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Power</p>
                <p class="mt-2 text-sm text-white">{{ featuredGarageCar.horsepower_tuned || featuredGarageCar.horsepower_stock || 'N/A' }}</p>
              </div>
            </div>
          </div>
          <div v-if="posts.length" class="mt-auto rounded-2xl border border-white/10 bg-midnight-900/60 p-4">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Spotlight</p>
            <router-link :to="`/post/${posts[0].id}`" class="mt-3 flex items-center gap-4">
              <div class="h-16 w-16 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/60">
                <img
                  :src="postMediaUrl(posts[0])"
                  :style="{ objectPosition: postMediaPosition(posts[0]) }"
                  alt="Post preview"
                  class="h-full w-full object-cover"
                />
              </div>
              <div class="min-w-0">
                <p class="truncate text-sm text-white">{{ posts[0].caption || 'Untitled build' }}</p>
                <p class="mt-1 text-xs text-steel-400">{{ posts[0].description || 'Tap to view build details.' }}</p>
              </div>
            </router-link>
          </div>
        </div>
        <div class="hidden space-y-5 rounded-2xl border border-white/10 bg-midnight-900/40 p-5 lg:block">
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Member Since</p>
            <p class="mt-2 text-sm text-steel-200">{{ memberSince }}</p>
          </div>
          <div v-if="profile.tags && profile.tags.length" class="border-t border-white/5 pt-5">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Interests</p>
            <div class="mt-3 flex flex-wrap gap-2">
              <span
                v-for="tag in profile.tags"
                :key="tag"
                class="inline-block rounded-full bg-neon-400/10 px-3 py-1 text-xs font-medium text-neon-400 border border-neon-400/20"
              >
                {{ tag }}
              </span>
            </div>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Role</p>
            <div class="mt-2">
              <span class="inline-block rounded-full px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] border" :class="roleBadgeClass">
                {{ roleLabel }}
              </span>
            </div>
          </div>
          <div v-if="profile.country">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Country</p>
            <p class="mt-2 text-sm text-steel-200">{{ profile.country }}</p>
          </div>
          <div v-if="profile.city">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">City</p>
            <p class="mt-2 text-sm text-steel-200">{{ profile.city }}</p>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Status</p>
            <p class="mt-2 text-sm text-steel-200">{{ statusLabel }}</p>
          </div>
        </div>
      </div>

      <div v-if="false && activeTab === 'garage'" class="mt-10 rounded-3xl border border-white/10 bg-midnight-800/50 p-6 shadow-glow">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Garage</p>
            <h2 class="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Member builds</h2>
            <p class="mt-2 text-sm text-steel-300">A quick look at the cars this member is building, driving, and showing off.</p>
          </div>
          <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-steel-400">
            Cars
            <span class="ml-2 text-sm font-semibold text-white">{{ garageCount }}</span>
          </div>
        </div>

        <div v-if="garageCars.length" class="mt-6 grid gap-4 lg:grid-cols-2">
          <div
            v-for="car in garageCars"
            :key="car.id"
            class="overflow-hidden rounded-3xl border border-white/10 bg-midnight-900/60"
          >
            <div class="aspect-[4/3] overflow-hidden bg-midnight-900/70">
              <img
                v-if="car.coverImage"
                :src="car.coverImage"
                :alt="garageCarTitle(car)"
                class="h-full w-full object-cover"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-steel-400">
                No garage photo
              </div>
            </div>
            <div class="space-y-4 p-5">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ car.buildStatusLabel }}</p>
                  <h3 class="mt-2 text-xl font-semibold text-white">{{ garageCarTitle(car) }}</h3>
                  <p v-if="car.trim" class="mt-1 text-sm text-steel-300">{{ car.trim }}</p>
                </div>
                <div class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-steel-300">
                  {{ car.year || 'Year TBD' }}
                </div>
              </div>
              <div class="grid gap-3 sm:grid-cols-2">
                <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Engine</p>
                  <p class="mt-2 text-sm text-white">{{ car.engine || 'Not added yet' }}</p>
                </div>
                <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Drivetrain</p>
                  <p class="mt-2 text-sm text-white">{{ car.drivetrain || 'Not added yet' }}</p>
                </div>
                <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">HP Stock</p>
                  <p class="mt-2 text-sm text-white">{{ car.horsepower_stock || '—' }}</p>
                </div>
                <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">HP Tuned</p>
                  <p class="mt-2 text-sm text-white">{{ car.horsepower_tuned || '—' }}</p>
                </div>
              </div>
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Mods</p>
                <p class="mt-2 whitespace-pre-line text-sm text-steel-200">{{ car.modifications || 'No modifications added yet.' }}</p>
              </div>

              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Build Journal</p>
                    <p class="mt-2 text-sm text-steel-300">
                      {{ getJournalEntriesForCar(car.id).length ? `${getJournalEntriesForCar(car.id).length} timeline entries.` : 'No build journal entries yet.' }}
                    </p>
                  </div>
                  <span
                    v-if="getJournalEntriesForCar(car.id).length"
                    class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300"
                  >
                    Latest updates
                  </span>
                </div>

                <div v-if="getJournalEntriesForCar(car.id).length" class="mt-4 space-y-3">
                  <div
                    v-for="entry in getJournalEntriesForCar(car.id).slice(0, 3)"
                    :key="entry.id"
                    class="rounded-2xl border border-white/10 bg-midnight-950/60 p-4"
                  >
                    <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center gap-2">
                          <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300">
                            {{ entry.entryTypeLabel }}
                          </span>
                          <span class="text-xs text-steel-400">{{ entry.entryDateLabel }}</span>
                          <span v-if="entry.mileageLabel" class="text-xs text-steel-400">{{ entry.mileageLabel }}</span>
                        </div>
                        <h4 class="mt-3 text-base font-semibold text-white">{{ entry.title || 'Untitled update' }}</h4>
                        <p class="mt-2 whitespace-pre-line text-sm text-steel-300">{{ entry.summary || 'No notes added yet.' }}</p>
                      </div>
                      <div
                        v-if="entry.imagePreview"
                        class="h-24 w-full overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70 sm:w-32"
                      >
                        <img
                          :src="entry.imagePreview"
                          :alt="entry.title || 'Build journal entry'"
                          class="h-full w-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="mt-6 rounded-3xl border border-dashed border-white/10 bg-white/[0.03] px-6 py-10 text-center">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Garage Empty</p>
          <p class="mt-3 text-sm text-steel-300">This member has not added any garage builds yet.</p>
        </div>
      </div>

      <div class="mt-10 border-y border-white/10 py-3">
        <div class="grid grid-cols-2">
          <button
            type="button"
            class="flex items-center justify-center gap-2 py-2 text-sm font-semibold transition"
            :class="activeTab === 'posts' ? 'border-b-2 border-white text-white' : 'text-steel-400'"
            @click="activeTab = 'posts'"
          >
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z" />
            </svg>
            Posts
          </button>
          <button
            type="button"
            class="flex items-center justify-center gap-2 py-2 text-sm font-semibold transition"
            :class="activeTab === 'garage' ? 'border-b-2 border-white text-white' : 'text-steel-400'"
            @click="activeTab = 'garage'"
          >
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M3 7h18" />
              <path d="M7 3v18" />
              <path d="M17 3v18" />
            </svg>
            Garage
          </button>
        </div>
      </div>

      <div v-if="activeTab === 'posts' && posts.length" class="grid grid-cols-3 gap-1 sm:gap-2">
        <div
          v-for="post in posts"
          :key="post.id"
          class="group relative overflow-hidden bg-midnight-900/60"
        >
          <router-link :to="`/post/${post.id}`" class="block">
            <div class="relative aspect-square w-full">
              <transition :name="post.mediaDirection === 'prev' ? 'media-swipe-prev' : 'media-swipe-next'" mode="out-in">
                <img
                  :key="postMediaUrl(post)"
                  :src="postMediaUrl(post)"
                  alt="Post"
                  class="h-full w-full object-cover"
                  :style="{ objectPosition: postMediaPosition(post) }"
                />
              </transition>
              <div
                v-if="post.mediaUrls?.length > 1"
                class="absolute right-2 top-2 rounded-full bg-black/55 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white"
              >
                {{ post.mediaIndex + 1 }}/{{ post.mediaUrls.length }}
              </div>
              <div class="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-100 sm:opacity-0 sm:transition sm:group-hover:opacity-100">
                <p class="truncate text-xs font-medium text-white">{{ post.caption || 'Untitled post' }}</p>
                <p class="text-[11px] text-white/75">{{ post.likeCount }} likes · {{ post.viewCount || 0 }} views</p>
              </div>
            </div>
          </router-link>
          <div class="absolute inset-x-2 top-2 flex items-start justify-between gap-2 opacity-100 sm:opacity-0 sm:transition sm:group-hover:opacity-100">
            <button
              v-if="post.mediaUrls?.length > 1"
              type="button"
              class="rounded-full border border-white/20 bg-black/45 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-white"
              @click.prevent="prevMedia(post)"
            >
              Prev
            </button>
            <button
              v-if="post.mediaUrls?.length > 1"
              type="button"
              class="ml-auto rounded-full border border-white/20 bg-black/45 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-white"
              @click.prevent="nextMedia(post)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      <div v-else-if="activeTab === 'posts'" class="mt-8 rounded-3xl border border-dashed border-white/10 bg-white/[0.03] px-6 py-10 text-center">
        <p class="text-xs uppercase tracking-[0.35em] text-steel-400">No Posts Yet</p>
        <p class="mt-3 text-sm text-steel-300">This profile does not have a public grid yet.</p>
      </div>

      <div v-if="activeTab === 'garage'" class="mt-8 rounded-3xl border border-white/10 bg-midnight-800/50 p-6 shadow-glow">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Garage</p>
            <h2 class="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Member builds</h2>
            <p class="mt-2 text-sm text-steel-300">A quick look at the cars this member is building, driving, and showing off.</p>
          </div>
          <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-steel-400">
            Cars
            <span class="ml-2 text-sm font-semibold text-white">{{ garageCount }}</span>
          </div>
        </div>

        <div v-if="garageCars.length" class="mt-6 grid gap-4 lg:grid-cols-2">
          <div
            v-for="car in garageCars"
            :key="car.id"
            class="overflow-hidden rounded-3xl border border-white/10 bg-midnight-900/60"
          >
            <div class="aspect-[4/3] overflow-hidden bg-midnight-900/70">
              <img
                v-if="car.coverImage"
                :src="car.coverImage"
                :alt="garageCarTitle(car)"
                class="h-full w-full object-cover"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-steel-400">
                No garage photo
              </div>
            </div>
            <div class="space-y-4 p-5">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ car.buildStatusLabel }}</p>
                  <h3 class="mt-2 text-xl font-semibold text-white">{{ garageCarTitle(car) }}</h3>
                  <p v-if="car.trim" class="mt-1 text-sm text-steel-300">{{ car.trim }}</p>
                </div>
                <div class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-steel-300">
                  {{ car.year || 'Year TBD' }}
                </div>
              </div>
              <div class="grid gap-3 sm:grid-cols-2">
                <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Engine</p>
                  <p class="mt-2 text-sm text-white">{{ car.engine || 'Not added yet' }}</p>
                </div>
                <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Drivetrain</p>
                  <p class="mt-2 text-sm text-white">{{ car.drivetrain || 'Not added yet' }}</p>
                </div>
                <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">HP Stock</p>
                  <p class="mt-2 text-sm text-white">{{ car.horsepower_stock || 'â€”' }}</p>
                </div>
                <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">HP Tuned</p>
                  <p class="mt-2 text-sm text-white">{{ car.horsepower_tuned || 'â€”' }}</p>
                </div>
              </div>
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Mods</p>
                <p class="mt-2 whitespace-pre-line text-sm text-steel-200">{{ car.modifications || 'No modifications added yet.' }}</p>
              </div>

              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Build Journal</p>
                    <p class="mt-2 text-sm text-steel-300">
                      {{ getJournalEntriesForCar(car.id).length ? `${getJournalEntriesForCar(car.id).length} timeline entries.` : 'No build journal entries yet.' }}
                    </p>
                  </div>
                  <span
                    v-if="getJournalEntriesForCar(car.id).length"
                    class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300"
                  >
                    Latest updates
                  </span>
                </div>

                <div v-if="getJournalEntriesForCar(car.id).length" class="mt-4 space-y-3">
                  <div
                    v-for="entry in getJournalEntriesForCar(car.id).slice(0, 3)"
                    :key="entry.id"
                    class="rounded-2xl border border-white/10 bg-midnight-950/60 p-4"
                  >
                    <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center gap-2">
                          <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300">
                            {{ entry.entryTypeLabel }}
                          </span>
                          <span class="text-xs text-steel-400">{{ entry.entryDateLabel }}</span>
                          <span v-if="entry.mileageLabel" class="text-xs text-steel-400">{{ entry.mileageLabel }}</span>
                        </div>
                        <h4 class="mt-3 text-base font-semibold text-white">{{ entry.title || 'Untitled update' }}</h4>
                        <p class="mt-2 whitespace-pre-line text-sm text-steel-300">{{ entry.summary || 'No notes added yet.' }}</p>
                      </div>
                      <div
                        v-if="entry.imagePreview"
                        class="h-24 w-full overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70 sm:w-32"
                      >
                        <img
                          :src="entry.imagePreview"
                          :alt="entry.title || 'Build journal entry'"
                          class="h-full w-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="mt-6 rounded-3xl border border-dashed border-white/10 bg-white/[0.03] px-6 py-10 text-center">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Garage Empty</p>
          <p class="mt-3 text-sm text-steel-300">This member has not added any garage builds yet.</p>
        </div>
      </div>

      <p v-if="errorMessage" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ errorMessage }}
      </p>
    </div>

    <div
      v-if="showFollowModal"
      class="fixed inset-0 z-[140] overflow-y-auto bg-black/70 px-4 py-6 sm:flex sm:items-center sm:justify-center sm:px-6"
      @click.self="closeFollowModal"
    >
      <div class="follow-modal-panel my-auto flex w-full max-w-md flex-col rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between gap-3">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Connections</p>
            <h3 class="mt-2 font-display text-xl font-semibold text-white">
              {{ followModalType === 'followers' ? 'Followers' : 'Following' }}
            </h3>
          </div>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeFollowModal"
          >
            Close
          </button>
        </div>

        <p v-if="followModalLoading" class="mt-4 text-sm text-steel-300">Loading...</p>
        <p v-else-if="followModalError" class="mt-4 text-sm text-rose-200">{{ followModalError }}</p>
        <p v-else-if="!followModalUsers.length" class="mt-4 text-sm text-steel-300">
          {{ followModalType === 'followers' ? 'No followers yet.' : 'Not following anyone yet.' }}
        </p>
        <div v-else class="follow-modal-list mt-4 space-y-3 overflow-y-auto pr-1">
          <router-link
            v-for="user in followModalUsers"
            :key="`${followModalType}-${user.id}`"
            :to="`/u/${user.handle}`"
            class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 transition hover:border-white/20"
            @click="closeFollowModal"
          >
            <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="truncate text-sm text-white">{{ user.fullName }}</p>
                <verified-badge v-if="user.verified" size="xs" />
              </div>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>
    </div>
    </div>

  </section>
</template>

<script>
import { supabase, getAvatarUrl, getPostImageUrl, refreshReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'
import { createUserNotification } from '@/lib/notifications'
import VerifiedBadge from '@/components/VerifiedBadge.vue'

export default {
  name: 'PublicProfileView',
  components: {
    VerifiedBadge,
  },
  data() {
    return {
      profile: {
        full_name: '',
        bio: '',
        avatar_url: '',
        avatar_path: '',
        cover_url: '',
        cover_path: '',
        cover_offset_y: 50,
        role: '',
        member_tier: 'member',
        status: '',
        created_at: '',
        instagram_handle: '',
        website_url: '',
        youtube_url: '',
        tiktok_url: '',
        featured_car_id: '',
        tags: [],
        pinned_status: '',
      },
      posts: [],
      errorMessage: '',
      totalLikes: 0,
      totalViews: 0,
      monthlyTopMembers: [],
      monthlyBadgeLabel: '',
      viewerId: '',
      isFollowing: false,
      followBusy: false,
      followerCount: 0,
      followingCount: 0,
      followFeatureReady: true,
      garageCars: [],
      buildJournalEntriesByCar: {},
      showFollowModal: false,
      followModalType: 'followers',
      followModalUsers: [],
      followModalLoading: false,
      followModalError: '',
      isCoverExpanded: false,
      activeTab: 'posts',
      pageLoading: true,
      visibilityHandler: null,
      isRefreshingView: false,
    }
  },
  computed: {
    handle() {
      return this.$route.params.handle
    },
    displayAvatarUrl() {
      return getAvatarUrl({ avatarPath: this.profile.avatar_path, avatarUrl: this.profile.avatar_url })
    },
    displayCoverUrl() {
      return getAvatarUrl({ avatarPath: this.profile.cover_path, avatarUrl: this.profile.cover_url })
    },
    coverObjectPosition() {
      const y = Number.isFinite(this.profile.cover_offset_y) ? this.profile.cover_offset_y : 50
      return `50% ${y}%`
    },
    initials() {
      if (!this.profile.full_name) return 'D'
      return this.profile.full_name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    memberSince() {
      if (!this.profile.created_at) return '—'
      return new Date(this.profile.created_at).toLocaleDateString()
    },
    instagramUrl() {
      if (!this.profile.instagram_handle) return ''
      const cleaned = this.profile.instagram_handle.replace(/^@/, '').trim()
      if (!cleaned) return ''
      return `https://www.instagram.com/${cleaned}`
    },
    postCount() {
      return this.posts.length
    },
    garageCount() {
      return this.garageCars.length
    },
    canShowFollowButton() {
      return Boolean(this.viewerId && this.profile?.id && this.viewerId !== this.profile.id && this.followFeatureReady)
    },
    canMessageUser() {
      return Boolean(this.viewerId && this.profile?.id && this.viewerId !== this.profile.id)
    },
    followerCountLabel() {
      return Number(this.followerCount || 0).toLocaleString()
    },
    followingCountLabel() {
      return Number(this.followingCount || 0).toLocaleString()
    },
    locationLabel() {
      return [this.profile.city, this.profile.country].filter(Boolean).join(', ')
    },
    totalLikesLabel() {
      return Number(this.totalLikes || 0).toLocaleString()
    },
    totalViewsLabel() {
      return Number(this.totalViews || 0).toLocaleString()
    },
    statusLabel() {
      return this.profile.status === 'banned' ? 'Banned' : 'Active'
    },
    roleLabel() {
      return (this.profile.role || 'member')
    },
    roleBadgeClass() {
      const role = (this.profile.role || 'member').toLowerCase()
      if (role === 'admin') return 'bg-rose-500/10 border-rose-500/30 text-rose-200'
      if (role === 'moderator') return 'bg-violet-400/10 border-violet-400/30 text-violet-200'
      return 'bg-neon-400/10 border-neon-400/20 text-neon-400'
    },
    memberTierLabel() {
      return `${(this.profile.member_tier || 'member').replace(/_/g, ' ')} tier`
    },
    tierBadgeClass() {
      const tier = (this.profile.member_tier || 'member').toLowerCase()
      if (tier === 'business') return 'border-fuchsia-400/35 bg-fuchsia-400/10 text-fuchsia-200'
      if (tier === 'verified') return 'border-sky-400/35 bg-sky-400/10 text-sky-200'
      return 'border-white/10 bg-white/[0.03] text-steel-300'
    },
    latestPostLabel() {
      if (!this.posts.length || !this.posts[0].created_at) return '—'
      return new Date(this.posts[0].created_at).toLocaleDateString()
    },
    socialLinks() {
      return [
        this.instagramUrl ? { label: 'Instagram', href: this.instagramUrl } : null,
        this.profile.website_url ? { label: 'Website', href: this.profile.website_url } : null,
        this.profile.youtube_url ? { label: 'YouTube', href: this.profile.youtube_url } : null,
        this.profile.tiktok_url ? { label: 'TikTok', href: this.profile.tiktok_url } : null,
      ].filter(Boolean)
    },
    featuredGarageCar() {
      return this.garageCars.find((car) => car.id === this.profile.featured_car_id) || null
    },
    achievementBadges() {
      const badges = []
      if (this.totalLikes >= 10) {
        badges.push({ label: '10 Likes', tone: 'border-white/10 bg-white/5 text-white' })
      }
      if (this.totalLikes >= 50) {
        badges.push({ label: '50 Likes', tone: 'border-neon-400/30 bg-neon-400/10 text-neon-300' })
      }
      if (this.totalLikes >= 100) {
        badges.push({ label: '100 Likes', tone: 'border-amber-400/40 bg-amber-400/10 text-amber-200' })
      }
      if (this.totalLikes >= 500) {
        badges.push({ label: '500 Likes', tone: 'border-amber-400/50 bg-amber-400/20 text-amber-100' })
      }
      if (this.totalLikes >= 1000) {
        badges.push({ label: '1K Likes', tone: 'border-yellow-300/60 bg-yellow-300/20 text-yellow-100' })
      }
      if (this.profile.verified) {
        badges.push({
          label: 'Verified',
          tone: 'border-sky-400/40 bg-sky-400/10 text-sky-300',
        })
      }
      if (this.profile.champions_league_winner) {
        badges.push({
          label: 'Champions League Winner',
          tone: 'border-amber-300/60 bg-amber-300/20 text-amber-100',
        })
      }
      if (this.monthlyTopMembers.length && this.profile?.id && this.monthlyTopMembers.includes(this.profile.id)) {
        badges.push({
          label: this.monthlyBadgeLabel || 'Top 3',
          tone: 'border-amber-400/40 bg-amber-400/10 text-amber-200',
        })
      }
      return badges
    },
    highlightItems() {
      if (this.garageCars.length) {
        return this.garageCars.slice(0, 4).map((car) => ({
          key: `garage-${car.id}`,
          label: car.model || car.make || 'Build',
          image: car.coverImage || '',
        }))
      }

      return [
        {
          key: 'empty-highlight',
          label: 'New',
          image: '',
        },
      ]
    },
  },
  async mounted() {
    await this.refreshViewData()

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.refreshViewData({ silent: true })
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
  },
  beforeUnmount() {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
  },
  watch: {
    '$route.params.handle': async function() {
      // Reset data before fetching new profile
      this.profile = {
        full_name: '',
        bio: '',
        avatar_url: '',
        avatar_path: '',
        cover_url: '',
        cover_path: '',
        cover_offset_y: 50,
        role: '',
        member_tier: 'member',
        verified: false,
        champions_league_winner: false,
        status: '',
        created_at: '',
        instagram_handle: '',
        website_url: '',
        youtube_url: '',
        tiktok_url: '',
        featured_car_id: '',
        tags: [],
        pinned_status: '',
      }
      this.posts = []
      this.totalLikes = 0
      this.totalViews = 0
      this.errorMessage = ''
      this.isFollowing = false
      this.followerCount = 0
      this.followingCount = 0
      this.buildJournalEntriesByCar = {}
      this.activeTab = 'posts'
      this.pageLoading = true
      await this.fetchProfile()
    },
  },
  methods: {
    async refreshViewData(options = {}) {
      if (this.isRefreshingView) return

      const { silent = false } = options
      this.isRefreshingView = true

      try {
        if (!silent) {
          this.pageLoading = true
        }

        const session = await refreshReadySession()
        this.viewerId = session?.user?.id || ''
        await this.fetchProfile()
      } finally {
        this.isRefreshingView = false
        if (!silent) {
          this.pageLoading = false
        }
      }
    },
    postMediaUrl(post) {
      return post.mediaUrls?.[post.mediaIndex] || post.imageUrl || ''
    },
    postMediaPosition(post) {
      const offset = post.mediaOffsets?.[post.mediaIndex] || post.mediaOffsets?.[0] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
    garageCarTitle(car) {
      return [car.make, car.model].filter(Boolean).join(' ') || 'Untitled build'
    },
    journalEntryTypeLabel(value) {
      const map = {
        update: 'Update',
        mod: 'Modification',
        maintenance: 'Maintenance',
        milestone: 'Milestone',
      }
      return map[value] || 'Update'
    },
    formatJournalDate(value) {
      if (!value) return 'Date TBD'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return 'Date TBD'
      return parsed.toLocaleDateString()
    },
    formatJournalMileage(value) {
      if (!Number.isFinite(value)) return ''
      return `${Number(value).toLocaleString()} mi`
    },
    garageBuildStatusLabel(value) {
      const map = {
        building: 'Building',
        street: 'Street',
        track: 'Track',
        show: 'Show',
        sale: 'For Sale',
      }
      return map[value] || 'Build'
    },
    garageImageUrl({ cover_image_path, cover_image_url }) {
      if (cover_image_path) {
        return getPostImageUrl({ imagePath: cover_image_path, imageUrl: cover_image_url })
      }
      return cover_image_url || ''
    },
    buildJournalImageUrl({ image_path, image_url }) {
      if (image_path) {
        return getPostImageUrl({ imagePath: image_path, imageUrl: image_url })
      }
      return image_url || ''
    },
    normalizeJournalEntry(entry) {
      return {
        ...entry,
        imagePreview: this.buildJournalImageUrl(entry),
        entryTypeLabel: this.journalEntryTypeLabel(entry.entry_type),
        entryDateLabel: this.formatJournalDate(entry.entry_date),
        mileageLabel: this.formatJournalMileage(entry.mileage),
      }
    },
    groupJournalEntries(entries) {
      return (entries || []).reduce((accumulator, entry) => {
        const key = entry.garage_car_id
        if (!key) return accumulator
        if (!accumulator[key]) {
          accumulator[key] = []
        }
        accumulator[key].push(this.normalizeJournalEntry(entry))
        return accumulator
      }, {})
    },
    getJournalEntriesForCar(carId) {
      return this.buildJournalEntriesByCar[carId] || []
    },
    nextMedia(post) {
      post.mediaDirection = 'next'
      post.mediaIndex = (post.mediaIndex + 1) % post.mediaUrls.length
    },
    prevMedia(post) {
      post.mediaDirection = 'prev'
      post.mediaIndex = (post.mediaIndex - 1 + post.mediaUrls.length) % post.mediaUrls.length
    },
    async fetchProfile() {
      this.errorMessage = ''

      try {
        const { data, error } = await runSupabaseQueryWithRetry(() => (
          supabase
            .from('profiles')
            .select('id, full_name, bio, avatar_url, avatar_path, cover_url, cover_path, cover_offset_y, role, member_tier, verified, champions_league_winner, status, country, city, created_at, instagram_handle, website_url, youtube_url, tiktok_url, featured_car_id, tags, pinned_status')
            .eq('handle', this.handle)
            .maybeSingle()
        ))

        if (error) {
          this.errorMessage = 'Profile data is not available yet.'
          return
        }

        if (!data) {
          this.errorMessage = 'This profile does not exist yet.'
          return
        }

        this.profile = data
        await this.fetchMonthlyTopMembers()
        await this.fetchFollowState()
        await this.fetchFollowCounts()
        await this.fetchGarageCars()
        await this.fetchBuildJournalEntries()
        await this.fetchPosts()
      } finally {
        this.pageLoading = false
      }
    },
    async fetchGarageCars() {
      if (!this.profile?.id) {
        this.garageCars = []
        return
      }

      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('garage_cars')
          .select('id, make, model, year, trim, engine, drivetrain, horsepower_stock, horsepower_tuned, modifications, build_status, cover_image_url, cover_image_path, created_at')
          .eq('user_id', this.profile.id)
          .order('created_at', { ascending: false })
      ))

      if (error) {
        this.garageCars = []
        return
      }

      this.garageCars = (data || []).map((car) => ({
        ...car,
        coverImage: this.garageImageUrl(car),
        buildStatusLabel: this.garageBuildStatusLabel(car.build_status),
      }))
    },
    async fetchBuildJournalEntries() {
      if (!this.profile?.id) {
        this.buildJournalEntriesByCar = {}
        return
      }

      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('build_journal_entries')
          .select('id, garage_car_id, title, summary, entry_type, entry_date, mileage, image_url, image_path, created_at')
          .eq('user_id', this.profile.id)
          .order('entry_date', { ascending: false })
          .order('created_at', { ascending: false })
      ))

      if (error) {
        this.buildJournalEntriesByCar = {}
        return
      }

      this.buildJournalEntriesByCar = this.groupJournalEntries(data || [])
    },
    async fetchFollowState() {
      if (!this.viewerId || !this.profile?.id || this.viewerId === this.profile.id) {
        this.isFollowing = false
        return
      }

      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('user_follows')
          .select('id')
          .eq('follower_id', this.viewerId)
          .eq('following_id', this.profile.id)
          .maybeSingle()
      ))

      if (error) {
        this.followFeatureReady = false
        this.isFollowing = false
        return
      }

      this.followFeatureReady = true
      this.isFollowing = Boolean(data?.id)
    },
    async fetchFollowCounts() {
      if (!this.profile?.id) return

      const [followersResult, followingResult] = await Promise.all([
        runSupabaseQueryWithRetry(() => (
          supabase
            .from('user_follows')
            .select('id', { count: 'exact', head: true })
            .eq('following_id', this.profile.id)
        )),
        runSupabaseQueryWithRetry(() => (
          supabase
            .from('user_follows')
            .select('id', { count: 'exact', head: true })
            .eq('follower_id', this.profile.id)
        )),
      ])

      if (followersResult.error || followingResult.error) {
        this.followFeatureReady = false
        this.followerCount = 0
        this.followingCount = 0
        return
      }

      this.followFeatureReady = true
      this.followerCount = followersResult.count || 0
      this.followingCount = followingResult.count || 0
    },
    formatFollowUsers(rows, key) {
      return (rows || []).map((row) => {
        const profile = row[key] || {}
        const fullName = profile.full_name || 'DRVN Member'
        return {
          id: profile.id || row.id,
          handle: profile.handle || 'member',
          fullName,
          avatar: getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url }),
          verified: Boolean(profile.verified),
          initials: fullName
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((chunk) => chunk[0].toUpperCase())
            .join(''),
        }
      })
    },
    async openFollowModal(type) {
      if (!this.profile?.id) return

      this.showFollowModal = true
      this.followModalType = type
      this.followModalUsers = []
      this.followModalError = ''
      this.followModalLoading = true

      const isFollowers = type === 'followers'
      const { data, error } = await supabase
        .from('user_follows')
        .select(
          isFollowers
            ? 'id, follower:profiles!user_follows_follower_id_fkey (id, handle, full_name, avatar_url, avatar_path, verified)'
            : 'id, following:profiles!user_follows_following_id_fkey (id, handle, full_name, avatar_url, avatar_path, verified)'
        )
        .eq(isFollowers ? 'following_id' : 'follower_id', this.profile.id)
        .order('created_at', { ascending: false })

      this.followModalLoading = false

      if (error) {
        this.followModalError = 'Unable to load this list right now.'
        return
      }

      this.followModalUsers = this.formatFollowUsers(data, isFollowers ? 'follower' : 'following')
    },
    toggleCoverExpanded() {
      if (!this.displayCoverUrl) return
      this.isCoverExpanded = !this.isCoverExpanded
    },
    closeFollowModal() {
      this.showFollowModal = false
      this.followModalUsers = []
      this.followModalError = ''
      this.followModalLoading = false
    },
    async toggleFollow() {
      if (!this.canShowFollowButton || this.followBusy) return

      this.followBusy = true
      this.errorMessage = ''

      if (this.isFollowing) {
        const { error } = await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', this.viewerId)
          .eq('following_id', this.profile.id)

        if (error) {
          this.errorMessage = error.message || 'Unable to update follow status.'
          this.followBusy = false
          return
        }

        this.isFollowing = false
        this.followerCount = Math.max(0, this.followerCount - 1)
      } else {
        const { error } = await supabase
          .from('user_follows')
          .insert({
            follower_id: this.viewerId,
            following_id: this.profile.id,
          })

        if (error) {
          this.errorMessage = error.message || 'Unable to follow this member right now.'
          this.followBusy = false
          return
        }

        this.isFollowing = true
        this.followerCount += 1
        await this.notifyFollow()
      }

      this.followBusy = false
    },
    async notifyFollow() {
      if (!this.profile?.id || this.profile.id === this.viewerId) return

      await createUserNotification({
        recipientId: this.profile.id,
        actorId: this.viewerId,
        type: 'follow',
      })
    },
    async fetchMonthlyTopMembers() {
      const now = new Date()
      if (now.getDate() !== 1) {
        this.monthlyTopMembers = []
        this.monthlyBadgeLabel = ''
        return
      }

      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 1)
      this.monthlyBadgeLabel = `Top 3 — ${start.toLocaleString(undefined, { month: 'short' })}`

      const { data: posts, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('posts')
          .select('user_id, post_likes (id), created_at')
          .gte('created_at', start.toISOString())
          .lt('created_at', end.toISOString())
      ))

      if (error || !posts?.length) {
        this.monthlyTopMembers = []
        return
      }

      const likeMap = new Map()
      ;(posts || []).forEach((post) => {
        const count = (post.post_likes || []).length || 0
        likeMap.set(post.user_id, (likeMap.get(post.user_id) || 0) + count)
      })

      this.monthlyTopMembers = Array.from(likeMap.entries())
        .map(([userId, count]) => ({ userId, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3)
        .map((entry) => entry.userId)
    },
    async fetchPosts() {
      if (!this.profile?.id) return
      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('posts')
          .select('id, caption, description, modifications, engine, hp_stock, hp_tuned, year, image_url, image_path, image_offset_x, image_offset_y, created_at, post_media (image_url, image_path, sort_order, offset_x, offset_y)')
          .eq('user_id', this.profile.id)
          .order('created_at', { ascending: false })
      ))

      if (error) return

      this.posts = (data || []).map((post) => {
        const mediaItems = [
          {
            url: post.image_path
              ? getPostImageUrl({ imagePath: post.image_path, imageUrl: post.image_url })
              : post.image_url,
            offset: {
              x: Number.isFinite(post.image_offset_x) ? post.image_offset_x : 50,
              y: Number.isFinite(post.image_offset_y) ? post.image_offset_y : 50,
            },
          },
          ...(post.post_media || []).map((item) => ({
            url: item.image_path
              ? getPostImageUrl({ imagePath: item.image_path, imageUrl: item.image_url })
              : item.image_url,
            offset: {
              x: Number.isFinite(item.offset_x) ? item.offset_x : 50,
              y: Number.isFinite(item.offset_y) ? item.offset_y : 50,
            },
          })),
        ].filter((item) => Boolean(item.url))

        return {
          ...post,
          imageUrl: post.image_path
            ? getPostImageUrl({ imagePath: post.image_path, imageUrl: post.image_url })
            : post.image_url,
          mediaUrls: mediaItems.map((item) => item.url),
          mediaOffsets: mediaItems.map((item) => item.offset),
          mediaIndex: 0,
          mediaDirection: 'next',
          likeCount: 0,
          viewCount: 0,
        }
      })
      await this.fetchPostLikeCounts()
      await this.fetchPostViewCounts()
      await this.fetchTotalLikes()
      await this.fetchTotalViews()
    },
    async fetchPostLikeCounts() {
      const postIds = this.posts.map((post) => post.id)
      if (!postIds.length) return

      const { data: likes } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('post_likes')
          .select('post_id')
          .in('post_id', postIds)
      ))

      const likeCounts = new Map()
      ;(likes || []).forEach((like) => {
        likeCounts.set(like.post_id, (likeCounts.get(like.post_id) || 0) + 1)
      })

      this.posts = this.posts.map((post) => ({
        ...post,
        likeCount: likeCounts.get(post.id) || 0,
      }))
    },
    async fetchTotalLikes() {
      const postIds = this.posts.map((post) => post.id)
      if (!postIds.length) {
        this.totalLikes = 0
        return
      }

      const { count } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('post_likes')
          .select('id', { count: 'exact', head: true })
          .in('post_id', postIds)
      ))

      this.totalLikes = count || 0
    },
    async fetchPostViewCounts() {
      const postIds = this.posts.map((post) => post.id)
      if (!postIds.length) return

      const { data: views } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('post_views')
          .select('post_id')
          .in('post_id', postIds)
      ))

      const viewCounts = new Map()
      ;(views || []).forEach((view) => {
        viewCounts.set(view.post_id, (viewCounts.get(view.post_id) || 0) + 1)
      })

      this.posts = this.posts.map((post) => ({
        ...post,
        viewCount: viewCounts.get(post.id) || 0,
      }))
    },
    async fetchTotalViews() {
      const postIds = this.posts.map((post) => post.id)
      if (!postIds.length) {
        this.totalViews = 0
        return
      }

      const { count } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('post_views')
          .select('id', { count: 'exact', head: true })
          .in('post_id', postIds)
      ))

      this.totalViews = count || 0
    },
  },
}
</script>

<style>
.instagram-ring {
  background: linear-gradient(135deg, rgba(250, 204, 21, 0.95), rgba(244, 114, 182, 0.9), rgba(96, 165, 250, 0.85));
}

.instagram-story-ring {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.05));
}

.cover-panel {
  min-height: 12rem;
  height: 12rem;
  transition: height 320ms ease, min-height 320ms ease;
}

.cover-panel-expanded {
  min-height: 24rem;
  height: 24rem;
}

@media (min-width: 640px) {
  .cover-panel {
    min-height: 15rem;
    height: 15rem;
  }

  .cover-panel-expanded {
    min-height: 30rem;
    height: 30rem;
  }
}

.media-swipe-next-enter-active,
.media-swipe-next-leave-active,
.media-swipe-prev-enter-active,
.media-swipe-prev-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.media-swipe-next-enter-from {
  opacity: 0;
  transform: translateX(10%);
}

.media-swipe-next-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-enter-from {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-leave-to {
  opacity: 0;
  transform: translateX(10%);
}

.follow-modal-panel {
  max-height: min(80vh, 42rem);
}

.follow-modal-list {
  min-height: 0;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
</style>

